/**
 * Whether captured waiver consent satisfies what the club requires.
 *
 * Its own module rather than living beside `WaiverSection` so that file exports
 * only a component (fast refresh), and so the two booking-creation paths import
 * the rule from one place.
 *
 * @module pages/booking/components/waiverCompletion
 */

import { isWaiverConsentComplete } from '@butter/ui/domains/legal';
import type { ActiveWaiver } from '~/services/internalApi/waiver';
import type { WaiverSignaturePaths } from '@lgc/database/types';

/**
 * True when every active waiver has the signature and checkbox it asks for.
 *
 * Shared by the CHECKOUT steps so they gate identically — the authenticated
 * member path submits from `PaymentDetailsStep`, the guest path from
 * `FinishAccountStep`, and only one of them being gated is the bug this function
 * exists to prevent.
 *
 * `BookingTimeStep` and `DemoTimeStep` create bookings straight from the time
 * step with no checkout, so they capture no consent — and this function handles
 * that without a second mechanism: with empty signature and agreement maps it
 * reduces to "the required list is empty", i.e. the server says nothing is
 * outstanding. All five creation sites therefore ask one question, through
 * `useWaiverSubmission`.
 */
export function areWaiversSatisfied(
  waivers: ActiveWaiver[],
  signatures: Record<number, WaiverSignaturePaths | null>,
  agreements: Record<number, boolean>,
): boolean {
  return waivers.every((waiver) =>
    isWaiverConsentComplete({
      requireSignature: waiver.requireSignature,
      requireAgreement: waiver.requireAgreement,
      signature: signatures[waiver.waiverVersionId] ?? null,
      agreed: agreements[waiver.waiverVersionId] ?? false,
    }),
  );
}

/**
 * What to tell someone the checkout gate just refused.
 *
 * Two messages, because there are two reasons and only one of them is actionable
 * by the visitor. Telling someone to sign a waiver that never rendered — because
 * the request for it failed — is a dead end: there is nothing on screen to sign.
 *
 * Lives here so all four refusal sites read the same, rather than four copies of
 * a string that drift.
 */
export function waiverRefusalMessage(answerMissing: boolean): string {
  return answerMissing
    ? "We couldn't confirm this club's waiver requirements. Please try again."
    : 'Please review and sign the waiver to continue.';
}
